"use strict";

import type { Category, DegreeSeekingProgram } from "~/server/resolvers/resolvers-types";
import type { RfiFormFieldShape } from "~/src/services/rfiSchema";
import type { FormattedProgram, RelationalFormattedProgram, WithRequired } from "~/types/types";
import { getSessionItem, setSessionItem } from "~/utils/sessionStorage";

const UNDECIDED_OPTION_TEXT = "Undecided";
const UNDECIDED_PROGRAM_OPTION_VALUE = "__rfi_undecided_program__";
const UNDECIDED_PROGRAM_CODE_BY_DEGREE_TYPE: Record<string, string> = {
  Undergraduate: "UGUD-UNDECIDED",
  Graduate: "GRAD-UNDECIDED",
  Certificates: "GRAD-UNDECIDED"
};
const UNDECIDED_DROPDOWN_DEGREE_TYPES = new Set(["Undergraduate"]);
const LEAD_API_RETRIES = 3;
const LEAD_API_RETRY_DELAY_MS = 1000;

export const useRfiService = () => {
  const config = useRuntimeConfig();

  // internal functions

  interface AsuoCookieData {
    utm_source: string | null;
    utm_medium: string | null;
    utm_term: string | null;
    utm_content: string | null;
    utm_campaign: string | null;
    utm_campaignid: string | null;
    utm_adid: string | null;
    utm_adgroupid: string | null;
    psfcid: string | null;
    gclickid: string | null;
    gclsrc: string | null;
  }
  /**
   * Gets attribution data from the current page URL only.
   * This prevents stale campaign values from old cookies being sent on direct visits.
   */
  function getASUOCookie(): AsuoCookieData {
    const result: AsuoCookieData = {
      utm_source: null,
      utm_medium: null,
      utm_term: null,
      utm_content: null,
      utm_campaign: null,
      utm_campaignid: null,
      utm_adid: null,
      utm_adgroupid: null,
      psfcid: null,
      gclickid: null,
      gclsrc: null
    };

    if (typeof window === "undefined") {
      return result;
    }

    const params = new URL(window.location.toString()).searchParams;

    (Object.keys(result) as Array<keyof AsuoCookieData>).forEach((key) => {
      if (!params.has(key)) {
        return;
      }

      const value = params.get(key);
      result[key] = value && value.trim().length > 0 ? value : null;
    });

    return result;
  }

  /**
   * Reads hidden input values and injects them into the form object
   */
  function populateHiddenFormFields(form: RfiFormFieldShape) {
    const reqIdEl = document.getElementById("cq_req_id") as HTMLInputElement | null;
    const reqIpEl = document.getElementById("cq_req_ip") as HTMLInputElement | null;

    // Prioritize live DOM values, fallback to sessionStorage
    const reqId = reqIdEl?.value || getSessionItem("cq_req_id") || "";
    const reqIp = reqIpEl?.value || getSessionItem("cq_req_ip") || "";

    form.cq_req_id = reqId;
    form.cq_req_ip = reqIp;
  }

  interface GtmClientIds {
    clientid: string;
    enterpriseclientid: string;
  }

  function getGtmClientIds(): GtmClientIds {
    const gaCookie = getCookie("_ga");
    let gaClientId = null;

    try {
      if (gaCookie && typeof gaCookie !== "undefined") {
        // sample value: GA1.1.2040707708.1641428898
        gaClientId = gaCookie.split(".").splice(2).join(".").toString();
      }
    } catch (error) {
      console.error("== error in getting client IDs: ", error);
    }
    return {
      clientid: gaClientId!,
      enterpriseclientid: gaClientId!
    };
  }

  /**
   * Returns GA CLient ID
   * sample value: GA1.1.2040707708.1641428898
   */
  function getGaClientId(): string {
    const gaCookie = getCookie("_ga");
    let gaClientId = "";

    try {
      if (gaCookie && typeof gaCookie !== "undefined") {
        // sample value: GA1.1.2040707708.1641428898
        gaClientId = gaCookie.split(".").splice(2).join(".").toString();
      }
    } catch (error) {
      console.error("== error in getting client IDs: ", error);
    }
    return gaClientId;
  }

  /**
   * Gets cookie using cookie's name
   * Example:
   * getCookie("_ga")
   */
  function getCookie(cname: string): string | null {
    const name = cname + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return null;
  }

  /**
   * sets name and program code in session storage
   * @param {{name: string, programCode: string}} myObj student name and program code
   */
  function setStudentSessionInfo({ name, programCode }: { name: string; programCode: string }) {
    setSessionItem("rfi-name", name);
    setSessionItem("rfi-program", programCode);
  }

  // exported functions

  /**
   * Maps all programs to an object
   */
  function generateAllProgramMap(programs: DegreeSeekingProgram[]) {
    const map: { [key: string]: DegreeSeekingProgram } = {};

    programs.forEach((program) => {
      map[program.id] = program;
    });

    return map;
  }

  function getAllFormattedPrograms(programs: DegreeSeekingProgram[]) {
    const result: WithRequired<FormattedProgram, "interestAreas">[] = [];

    programs.forEach((program) => {
      const areas: string[] = [];
      program.interestAreas!.forEach((area) => {
        areas.push(area!.title!);
      });
      result.push({
        id: program.id,
        code: program.code!,
        title: program.title!,
        category: program.category?.title ?? "",
        interestAreas: areas
      });
    });
    return result;
  }

  function getAllFormattedInterestAreas(
    interestAreaEdges: Array<Pick<DegreeSeekingProgram, "title">>
  ) {
    if (interestAreaEdges && interestAreaEdges.length > 0) {
      return interestAreaEdges.map((item) => item.title!);
    } else {
      return [""];
    }
  }

  function getAllFormattedCategories(categorys: Array<Pick<Category, "title">>): string[] {
    return categorys.map((item) => item.title!);
  }

  function getDegreeTypePrograms(
    allPrograms: WithRequired<FormattedProgram, "interestAreas">[],
    degreeType: string
  ) {
    return allPrograms.filter((item) => item.category === degreeType);
  }
  function getDegreeTypeInterestAreas(
    allPrograms: Array<WithRequired<FormattedProgram, "interestAreas">>,
    degreeType: string
  ): string[] {
    /**
     * @type {string[]}
     */
    const result: string[] = [];
    const degreeTypePrograms = getDegreeTypePrograms(allPrograms, degreeType);
    degreeTypePrograms.forEach((item) => {
      // interestAreas is an array of string
      item.interestAreas.forEach((area) => {
        if (result.indexOf(area) === -1) {
          result.push(area);
        }
      });
    });
    return result;
  }

  function getInterestAreaPrograms(
    allPrograms: Array<WithRequired<FormattedProgram, "interestAreas">>,
    interestArea: string
  ) {
    return allPrograms.filter((item) => {
      return item.interestAreas.indexOf(interestArea) > -1;
    });
  }

  /**
   *
   * @param allDegreeTypes - array of degree types as strings
   * @param  allPrograms - array of formatted programs
   * @returns  Returns an object of degree types with objects of interest areas and their associated formatted programs.
   */
  function getRelationalProgramData(
    allDegreeTypes: string[],
    allPrograms: Array<WithRequired<FormattedProgram, "interestAreas">>
  ): RelationalFormattedProgram {
    const relationalData: RelationalFormattedProgram = {};

    allDegreeTypes.forEach((type) => {
      relationalData[type as keyof RelationalFormattedProgram] = {};
    });

    Object.keys(relationalData).forEach((key) => {
      const areas = getDegreeTypeInterestAreas(allPrograms, key);

      areas.forEach((area) => {
        relationalData[key][area] = [];
      });
    });

    allPrograms.forEach((program) => {
      program.interestAreas.forEach((interestArea) => {
        relationalData[program.category][interestArea].push(program);
      });
    });
    return relationalData;
  }

  function getDegreeTypeOptions(degreeTypes: string[]) {
    const options: Array<{ value: string; text: string }> = [];
    // Make sure the values are exactly the same as the keys else the values will be duplicated in the options
    const degreeOrder = ["Undergraduate", "Graduate", "Certificates"];

    const orderedDegreeTypeSet = new Set(degreeOrder);
    const orderedDegreeTypes = [
      ...degreeOrder,
      ...degreeTypes.filter((type) => !orderedDegreeTypeSet.has(type))
    ];
    orderedDegreeTypes.forEach((item) => {
      options.push({
        value: item,
        text: item
      });
    });
    return options;
  }

  function sortAlphabeticallyStrings(arr: string[] = []) {
    return Array.isArray(arr)
      ? arr.slice().sort((a, b) =>
          a.localeCompare(b, undefined, {
            sensitivity: "base",
            numeric: true
          })
        )
      : [];
  }

  /**
   * @param  interestAreas - string array of interest areas
   */
  function getInterestAreaOptions(
    interestAreas: string[],
    config: { includeUndecided?: boolean } = {}
  ) {
    const raw = Array.isArray(interestAreas) ? interestAreas : [];
    const deduped = Array.from(new Set(raw));
    const sorted = sortAlphabeticallyStrings(
      config.includeUndecided ? deduped.filter((label) => label !== UNDECIDED_OPTION_TEXT) : deduped
    );
    const formattedOptions = sorted.map((label) => ({
      value: label,
      text: label
    }));
    return config.includeUndecided
      ? [
          {
            value: UNDECIDED_OPTION_TEXT,
            text: UNDECIDED_OPTION_TEXT
          },
          ...formattedOptions
        ]
      : formattedOptions;
  }

  /**
   * @param  programs - string array of program names
   */
  function getProgramOptions(
    programs: FormattedProgram[],
    config: { includeUndecided?: boolean } = {}
  ) {
    const formattedOptions: Array<{ value: string; text: string }> = [];
    programs.forEach((item) => {
      formattedOptions.push({
        value: item.id,
        text: item.title
      });
    });

    return config.includeUndecided
      ? [
          {
            value: UNDECIDED_PROGRAM_OPTION_VALUE,
            text: UNDECIDED_OPTION_TEXT
          },
          ...formattedOptions
        ]
      : formattedOptions;
  }

  function getProgramNameFromCode(
    allPrograms: FormattedProgram[],
    programCode: string
  ): string | "Select a program" {
    if (isUndecidedProgramCode(programCode)) {
      return UNDECIDED_OPTION_TEXT;
    }

    const result = allPrograms.filter((program) => {
      return program.code === programCode;
    });
    if (result.length > 0) {
      return result[0].title;
    }
    return "Select a program";
  }

  function getProgramCode(
    allPrograms: FormattedProgram[],
    programId: string,
    degreeType?: string | null
  ): string | "N/A" {
    if (isUndecidedProgramValue(programId)) {
      return getUndecidedProgramCode(degreeType) || "N/A";
    }

    const result = allPrograms.filter((program) => {
      return program.id === programId;
    });
    if (result.length > 0) {
      return result[0].code;
    }
    return "N/A";
  }

  function getProgramById(
    allPrograms: FormattedProgram[],
    programId: string
  ): FormattedProgram | null {
    return allPrograms.find((program) => program.id === programId) || null;
  }

  function getProgramInterestAreas(allPrograms: FormattedProgram[], programId: string): string[] {
    return getProgramById(allPrograms, programId)?.interestAreas || [];
  }

  function getPrimaryInterestAreaByProgramId(
    allPrograms: FormattedProgram[],
    programId: string
  ): string | null {
    return getProgramInterestAreas(allPrograms, programId)[0] || null;
  }

  function getUndecidedProgramCode(degreeType?: string | null): string | null {
    if (!degreeType) {
      return null;
    }

    return UNDECIDED_PROGRAM_CODE_BY_DEGREE_TYPE[degreeType] || null;
  }

  function canUseUndecidedProgram(degreeType?: string | null): boolean {
    return Boolean(getUndecidedProgramCode(degreeType));
  }

  function canShowUndecidedOption(degreeType?: string | null): boolean {
    return Boolean(degreeType && UNDECIDED_DROPDOWN_DEGREE_TYPES.has(degreeType));
  }

  function isUndecidedProgramCode(programCode?: string | null): boolean {
    if (!programCode) {
      return false;
    }

    return Object.values(UNDECIDED_PROGRAM_CODE_BY_DEGREE_TYPE).includes(programCode);
  }

  function isUndecidedProgramValue(programValue?: string | null): boolean {
    return programValue === UNDECIDED_PROGRAM_OPTION_VALUE;
  }

  function isUndecidedInterestAreaValue(interestArea?: string | null): boolean {
    return interestArea === UNDECIDED_OPTION_TEXT;
  }

  function getUrlParams(url: string) {
    const params: { [key: string]: string | number } = {};

    url.replace(/[?&]+([^=&]+)=([^&]*)/gi, function (m, key, value) {
      return (params[key] = value);
    });

    return params;
  }

  /**
   * Generates Form tracking data for GA Datalayer
   * @param  section
   * @param  text
   * @param   form - RFI form data
   * @param  career
   * @param  programName
   * @param  programCode
   */
  function getFormTrackingData(
    section: string,
    text: string,
    form: RfiFormFieldShape,
    career: string,
    programName: string,
    programCode: string
  ) {
    return {
      event: "form",
      name: "onclick",
      action: "click",
      type: "click",
      region: "main content",
      section: section,
      text: text,
      first_name: form.firstName!.toLowerCase(),
      last_name: form.lastName!.toLowerCase(),
      email_address: form.email!.toLowerCase(),
      phone: form.phone.formattedNumber,
      career: career.toLowerCase(),
      area_of_interest: form.interestArea?.text?.toLowerCase() || "",
      program: programName.toLowerCase(),
      program_code: programCode,
      military_service: form.isMilitary === false ? "no" : "yes"
    };
  }
  /**
   * Generates Form tracking data for GA Datalayer, specific to forms with military checkboxes.
   * @param  section
   * @param  text
   * @param  form - RFI form data
   * @param  career
   * @param  programName
   * @param  programCode
   * @param  isMilitary
   */
  function getMilitaryFormTrackingData(
    section: string,
    text: string,
    form: RfiFormFieldShape,
    career: string,
    programName: string,
    programCode: string,
    isMilitary: boolean
  ) {
    return {
      event: "form",
      name: "onclick",
      action: "click",
      type: "click",
      region: "main content",
      section: section,
      text: text,
      first_name: form.firstName!.toLowerCase(),
      last_name: form.lastName!.toLowerCase(),
      email_address: form.email!.toLowerCase(),
      phone: form.phone.formattedNumber,
      career: career.toLowerCase(),
      area_of_interest: form.interestArea?.text?.toLowerCase() || "",
      program: programName.toLowerCase(),
      program_code: programCode,
      military_service: isMilitary
    };
  }

  function formatPhoneNumber(phone: string) {
    // removing the `+`, then removing the spaces
    return phone ? phone.slice(1).replace(/ /g, "") : null;
  }
  /**
   * Generates Lead Data for Military forms that are sent to the Lead API
   * @param  form - RFI form data
   * @param programCode
   */
  function getMilitaryLeadData(
    form: RfiFormFieldShape,
    programCode: string,
    options: {
      degreeType?: string | null;
      areaOfInterest?: string | null;
      programName?: string | null;
    } = {}
  ) {
    populateHiddenFormFields(form);
    const gtmData = getGtmClientIds();
    const asuoCookie = getASUOCookie();

    setStudentSessionInfo({ name: form.firstName!, programCode: programCode });

    return {
      first_name: form.firstName,
      last_name: form.lastName,
      email_address: form.email,
      phone: formatPhoneNumber(form.phone.formattedNumber!),
      program_key: programCode,
      program: options.programName || "",
      degree_type: options.degreeType || "",
      area_of_interest: options.areaOfInterest || "",
      origin_uri: window ? window.location.toString() : "",
      sourceid: config.public.leadSourceId,
      sms_permission: true,
      utm_ecd22: getCookie("utm_ecd22"),
      ...gtmData,
      ...asuoCookie,
      military_service: form.isMilitary,
      cq_req_id: form.cq_req_id,
      cq_req_ip: form.cq_req_ip
    };
  }

  /**
   * Passes Lead Data up to lead api
   */
  function addRFILead(leadData: ReturnType<typeof getMilitaryLeadData>) {
    if (!config.public.leadApiPath) {
      console.error("Lead API Path Env Variable Missing");
      return;
    }

    return $fetch(config.public.leadApiPath, {
      baseURL: config.public.leadApiBaseUrl,
      method: "POST",
      headers: {
        Accept: "application/json"
      },
      body: leadData,
      retry: LEAD_API_RETRIES,
      retryDelay: LEAD_API_RETRY_DELAY_MS,
      retryStatusCodes: []
    });
  }

  /**
   * Gets the career value for a specific program code from a list of programs.
   * @param {string} programCode - The program code to search for.
   * @param {Array<FormattedProgram>} programs - The list of programs to search through.
   * @returns {string | null} - The career value associated with the program code, or null if not found.
   */
  function getCareerValueByProgramCode(
    programCode: string,
    programs: FormattedProgram[]
  ): string | null {
    const program = programs.find((p) => p.code === programCode);
    return program && program.category !== undefined ? program.category : null;
  }

  return {
    addRFILead: addRFILead,
    getFormTrackingData: getFormTrackingData,
    getGtmClientIds: getGtmClientIds,
    getUrlParams: getUrlParams,
    getProgramCode: getProgramCode,
    getProgramNameFromCode: getProgramNameFromCode,
    getPrimaryInterestAreaByProgramId: getPrimaryInterestAreaByProgramId,
    getProgramOptions: getProgramOptions,
    getInterestAreaOptions: getInterestAreaOptions,
    getDegreeTypeOptions: getDegreeTypeOptions,
    getRelationalProgramData: getRelationalProgramData,
    getInterestAreaPrograms: getInterestAreaPrograms,
    getDegreeTypeInterestAreas: getDegreeTypeInterestAreas,
    getDegreeTypePrograms: getDegreeTypePrograms,
    getAllFormattedCategories: getAllFormattedCategories,
    getAllFormattedInterestAreas: getAllFormattedInterestAreas,
    getAllFormattedPrograms: getAllFormattedPrograms,
    generateAllProgramMap: generateAllProgramMap,
    getMilitaryFormTrackingData: getMilitaryFormTrackingData,
    getMilitaryLeadData: getMilitaryLeadData,
    canUseUndecidedProgram: canUseUndecidedProgram,
    canShowUndecidedOption: canShowUndecidedOption,
    getUndecidedProgramCode: getUndecidedProgramCode,
    isUndecidedProgramCode: isUndecidedProgramCode,
    isUndecidedProgramValue: isUndecidedProgramValue,
    isUndecidedInterestAreaValue: isUndecidedInterestAreaValue,
    undecidedOptionText: UNDECIDED_OPTION_TEXT,
    undecidedProgramOptionValue: UNDECIDED_PROGRAM_OPTION_VALUE,
    getGaClientId: getGaClientId,
    getCareerValueByProgramCode: getCareerValueByProgramCode
  };
};
